import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, Sparkles, Upload, X, FileImage, FileText,
  Loader2, Check, AlertTriangle,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { usePricing } from "@/lib/PricingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALLOWED = [".pdf", ".png", ".jpg", ".jpeg", ".webp"];
const MAX_BYTES = 8 * 1024 * 1024;

// ---------- localized copy ---------- //
const COPY = {
  de: {
    badge: "NEU · KI-VISION",
    overline: "— KI-Zeichnungs-Analyse",
    title_1: "Zeichnung droppen.",
    title_2: "KI liest mit.",
    intro:
      "Ziehen Sie Ihre Bauteilzeichnung (PDF, PNG oder JPG) hier hinein — unsere KI erkennt Material, Masse, Gewinde und Toleranzen und schlägt das passende Veredelungsverfahren inkl. Richtpreis vor. In unter 20 Sekunden.",
    dropzone_title: "Zeichnung hier ablegen",
    dropzone_sub: "oder Datei wählen — PDF · PNG · JPG · WEBP · max. 8 MB",
    choose_file: "Datei wählen",
    analyzing_steps: [
      "Datei wird gelesen …",
      "Vision-Modell analysiert Geometrie …",
      "Werkstoff wird bestimmt …",
      "Passendes Verfahren wird abgeglichen …",
      "Richtpreis wird berechnet …",
    ],
    fallback_analyzing: "Analyse läuft …",
    section_specs: "Erkannte Kenndaten",
    section_recommendation: "Empfehlung",
    section_pricing: "Richtpreis",
    label_material: "Material",
    label_geometry: "Geometrie",
    label_dimensions: "Masse",
    label_threads: "Gewinde",
    label_tolerances: "Toleranzen",
    label_size: "Grössenklasse",
    label_reasoning: "Warum",
    label_alternate: "Auch möglich",
    label_confidence: "Konfidenz",
    label_notes: "Hinweis",
    price_of: "für",
    price_of_pieces: "Stück",
    qty_helper: "Angenommene Stückzahl (anpassbar)",
    cta_offer: "Verbindliche Offerte anfordern",
    cta_reset: "Neue Zeichnung analysieren",
    disclaimer:
      "Die KI-Analyse dient als Erstindikation. Der verbindliche Vorschlag inkl. Machbarkeit und Preis erfolgt durch unser Fachteam nach Prüfung Ihrer Original-Unterlagen.",
    err_format: (list) => `Format nicht erlaubt. Erlaubt: ${list}`,
    err_size: "Datei zu gross (max. 8 MB).",
    err_generic: "Analyse fehlgeschlagen. Bitte erneut versuchen.",
    high: "Hoch", medium: "Mittel", low: "Niedrig",
  },
  en: {
    badge: "NEW · AI VISION",
    overline: "— AI Drawing Analysis",
    title_1: "Drop a drawing.",
    title_2: "AI reads it.",
    intro:
      "Drag your component drawing (PDF, PNG or JPG) into the zone — our AI detects material, dimensions, threads and tolerances and recommends the right finishing process with a ballpark price. In under 20 seconds.",
    dropzone_title: "Drop drawing here",
    dropzone_sub: "or select a file — PDF · PNG · JPG · WEBP · max. 8 MB",
    choose_file: "Choose file",
    analyzing_steps: [
      "Reading file …",
      "Vision model analyzes geometry …",
      "Material identified …",
      "Matching finishing process …",
      "Ballpark price calculated …",
    ],
    fallback_analyzing: "Analyzing …",
    section_specs: "Detected specifications",
    section_recommendation: "Recommendation",
    section_pricing: "Ballpark price",
    label_material: "Material",
    label_geometry: "Geometry",
    label_dimensions: "Dimensions",
    label_threads: "Threads",
    label_tolerances: "Tolerances",
    label_size: "Size class",
    label_reasoning: "Why",
    label_alternate: "Also possible",
    label_confidence: "Confidence",
    label_notes: "Note",
    price_of: "for",
    price_of_pieces: "pieces",
    qty_helper: "Assumed quantity (editable)",
    cta_offer: "Request a binding quote",
    cta_reset: "Analyze another drawing",
    disclaimer:
      "The AI analysis is a first indication. The binding proposal — feasibility and price — comes from our team after review of your original documents.",
    err_format: (list) => `Format not allowed. Allowed: ${list}`,
    err_size: "File too large (max. 8 MB).",
    err_generic: "Analysis failed. Please try again.",
    high: "High", medium: "Medium", low: "Low",
  },
  fr: {
    badge: "NOUVEAU · IA VISION",
    overline: "— Analyse IA du plan",
    title_1: "Déposez un plan.",
    title_2: "L'IA le lit.",
    intro:
      "Glissez votre plan de pièce (PDF, PNG ou JPG) — notre IA détecte matériau, dimensions, filetages et tolérances et recommande le procédé de finition avec un prix indicatif. En moins de 20 secondes.",
    dropzone_title: "Déposez le plan ici",
    dropzone_sub: "ou sélectionnez un fichier — PDF · PNG · JPG · WEBP · max. 8 Mo",
    choose_file: "Choisir un fichier",
    analyzing_steps: [
      "Lecture du fichier …",
      "Le modèle vision analyse la géométrie …",
      "Matériau identifié …",
      "Recherche du procédé adapté …",
      "Prix indicatif calculé …",
    ],
    fallback_analyzing: "Analyse en cours …",
    section_specs: "Caractéristiques détectées",
    section_recommendation: "Recommandation",
    section_pricing: "Prix indicatif",
    label_material: "Matériau",
    label_geometry: "Géométrie",
    label_dimensions: "Dimensions",
    label_threads: "Filetages",
    label_tolerances: "Tolérances",
    label_size: "Classe de taille",
    label_reasoning: "Pourquoi",
    label_alternate: "Aussi possible",
    label_confidence: "Confiance",
    label_notes: "Remarque",
    price_of: "pour",
    price_of_pieces: "pièces",
    qty_helper: "Quantité supposée (modifiable)",
    cta_offer: "Demander un devis ferme",
    cta_reset: "Analyser un autre plan",
    disclaimer:
      "L'analyse IA est une première indication. La proposition ferme — faisabilité et prix — est établie par notre équipe après contrôle de vos documents.",
    err_format: (list) => `Format non autorisé. Autorisés : ${list}`,
    err_size: "Fichier trop volumineux (max. 8 Mo).",
    err_generic: "Analyse échouée. Veuillez réessayer.",
    high: "Élevée", medium: "Moyenne", low: "Faible",
  },
  it: {
    badge: "NUOVO · IA VISION",
    overline: "— Analisi IA del disegno",
    title_1: "Rilasci un disegno.",
    title_2: "L'IA lo legge.",
    intro:
      "Trascini il Suo disegno del componente (PDF, PNG o JPG) — la nostra IA rileva materiale, dimensioni, filettature e tolleranze e consiglia il procedimento di finitura con un prezzo indicativo. In meno di 20 secondi.",
    dropzone_title: "Rilasci il disegno qui",
    dropzone_sub: "o scelga un file — PDF · PNG · JPG · WEBP · max. 8 MB",
    choose_file: "Scegli file",
    analyzing_steps: [
      "Lettura del file …",
      "Il modello vision analizza la geometria …",
      "Materiale identificato …",
      "Ricerca del procedimento adatto …",
      "Prezzo indicativo calcolato …",
    ],
    fallback_analyzing: "Analisi in corso …",
    section_specs: "Dati rilevati",
    section_recommendation: "Raccomandazione",
    section_pricing: "Prezzo indicativo",
    label_material: "Materiale",
    label_geometry: "Geometria",
    label_dimensions: "Dimensioni",
    label_threads: "Filettature",
    label_tolerances: "Tolleranze",
    label_size: "Classe dimensionale",
    label_reasoning: "Perché",
    label_alternate: "Anche possibile",
    label_confidence: "Confidenza",
    label_notes: "Nota",
    price_of: "per",
    price_of_pieces: "pezzi",
    qty_helper: "Quantità ipotizzata (modificabile)",
    cta_offer: "Richiedi un'offerta vincolante",
    cta_reset: "Analizza un altro disegno",
    disclaimer:
      "L'analisi IA è una prima indicazione. La proposta vincolante — fattibilità e prezzo — viene emessa dal nostro team dopo verifica dei documenti originali.",
    err_format: (list) => `Formato non consentito. Ammessi: ${list}`,
    err_size: "File troppo grande (max. 8 MB).",
    err_generic: "Analisi non riuscita. Riprovi.",
    high: "Alta", medium: "Media", low: "Bassa",
  },
};

// map lowercase pricing key -> canonical Verfahren name used in Contact form
const VERFAHREN_TO_SELECT = {
  verzinken: "Verzinken",
  trommelverzinken: "Trommelverzinken",
  "zink-nickel": "Zink-Nickel",
  "zink-mangan": "Zink-Mangan-Phosphatieren",
  "cns-beizen": "CNS-Beizen",
  "cns-elektropolieren": "CNS-Elektropolieren",
  "schleifen-buersten": "Schleifen · Bürsten",
  bruenieren: "Brünieren",
  vergolden: "Vergolden",
  "chemisch-nickel": "Chemisch-Nickel",
  "galvanisch-nickel": "Galvanisch-Nickel",
};

function verfahrenDisplay(PRICING, key, lang) {
  if (!key || !PRICING[key]) return key || "—";
  return PRICING[key][`label_${lang}`] || PRICING[key].label_de;
}
function sizeDisplay(SIZE_CLASSES, key, lang) {
  if (!key || !SIZE_CLASSES[key]) return key || "—";
  return SIZE_CLASSES[key][`label_${lang}`] || SIZE_CLASSES[key].label_de;
}

// ---------- Component ---------- //
export const KiZeichnungsAnalyse = () => {
  const { lang } = useI18n();
  const { PRICING, SIZE_CLASSES, computePrice, formatCHF } = usePricing();
  const c = COPY[lang] || COPY.de;

  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [quantity, setQuantity] = useState(250);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const stepTimerRef = useRef(null);

  // rotate the "analyzing" steps while busy for a cinematic feel
  useEffect(() => {
    if (busy) {
      setStepIdx(0);
      stepTimerRef.current = setInterval(() => {
        setStepIdx((i) => Math.min(i + 1, c.analyzing_steps.length - 1));
      }, 2600);
    } else {
      clearInterval(stepTimerRef.current);
    }
    return () => clearInterval(stepTimerRef.current);
  }, [busy, c.analyzing_steps.length]);

  const validate = useCallback((f) => {
    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (!ALLOWED.includes(ext)) { toast.error(c.err_format(ALLOWED.join(", "))); return false; }
    if (f.size > MAX_BYTES) { toast.error(c.err_size); return false; }
    return true;
  }, [c]);

  const submit = useCallback(async (f) => {
    if (!validate(f)) return;
    setFile(f);
    setAnalysis(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/analyze-drawing`, fd, { timeout: 120000 });
      const a = res?.data?.analysis;
      if (!a) throw new Error("empty");
      setAnalysis(a);
      // pre-fill sensible quantity from confidence / size (small keeps default 250; larger classes → 100; xl → 25)
      const q = { micro: 500, small: 250, medium: 100, large: 50, xl: 25 }[a.estimated_size_class] || 250;
      setQuantity(q);
    } catch (err) {
      const detail = err?.response?.data?.detail || c.err_generic;
      toast.error(typeof detail === "string" ? detail : c.err_generic);
      setFile(null);
    } finally {
      setBusy(false);
    }
  }, [validate, c.err_generic]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) submit(f);
  }, [submit]);

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (f) submit(f);
    e.target.value = "";
  };

  const reset = () => {
    setFile(null);
    setAnalysis(null);
    setBusy(false);
  };

  const requestOffer = () => {
    if (!analysis) return;
    const verfKey = analysis.recommended_verfahren;
    const pricing = computePrice({
      verfahren: verfKey,
      size: analysis.estimated_size_class,
      quantity,
      urgency: "standard",
    });
    const rangeStr = pricing
      ? `CHF ${formatCHF(pricing.totalPriceMin)} – ${formatCHF(pricing.totalPriceMax)}`
      : "—";
    const message =
      `KI-Zeichnungs-Analyse:\n` +
      `• Datei: ${file?.name || "—"}\n` +
      `• Erkanntes Material: ${analysis.detected_material || "—"}\n` +
      `• Geometrie: ${analysis.detected_geometry || "—"}\n` +
      `• Masse: ${analysis.detected_dimensions || "—"}\n` +
      `• Gewinde: ${(analysis.detected_threads || []).join(", ") || "—"}\n` +
      `• Toleranzen: ${analysis.detected_tolerances || "—"}\n` +
      `• Empfohlenes Verfahren: ${PRICING[verfKey]?.label_de || verfKey}\n` +
      `• Angenommene Stückzahl: ${quantity}\n` +
      `• Richtpreis: ${rangeStr}\n\n` +
      `Bitte um verbindliche Offerte und Machbarkeitsprüfung.`;

    try {
      window.dispatchEvent(new CustomEvent("fk:prefill-contact", {
        detail: {
          service_interest: VERFAHREN_TO_SELECT[verfKey] || "",
          message,
        },
      }));
    } catch (_) { /* ignore */ }
    document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
  };

  const pricing = analysis && analysis.recommended_verfahren && analysis.estimated_size_class
    ? computePrice({
        verfahren: analysis.recommended_verfahren,
        size: analysis.estimated_size_class,
        quantity,
        urgency: "standard",
      })
    : null;

  const confLabel = analysis?.confidence === "high" ? c.high
    : analysis?.confidence === "medium" ? c.medium
    : analysis?.confidence === "low" ? c.low : "—";

  const confColor = analysis?.confidence === "high"
    ? "text-emerald-300 border-emerald-400/40 bg-emerald-400/5"
    : analysis?.confidence === "medium"
    ? "text-amber-300 border-amber-400/40 bg-amber-400/5"
    : "text-rose-300 border-rose-400/40 bg-rose-400/5";

  return (
    <section
      id="ki-analyse"
      data-testid="ki-analyse-section"
      className="relative bg-[#0B0B0D] py-24 md:py-36 border-t border-white/5 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-40 left-1/4 w-[500px] h-[500px] rounded-full bg-white/[0.03] blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="overline reveal mb-5 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 px-2 py-1 border border-white/15 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-white pulse-dot" />
                {c.badge}
              </span>
              <span>{c.overline}</span>
            </div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[0.95] text-white">
              {c.title_1}
              <br />
              <span className="text-zinc-500">{c.title_2}</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              {c.intro}
            </p>
          </div>
        </div>

        {/* Interactive card */}
        <div className="reveal bg-[#121214] border border-white/10">
          {/* --- STATE A: empty dropzone --- */}
          {!file && !busy && !analysis && (
            <label
              htmlFor="ki-file"
              data-testid="ki-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`flex flex-col items-center justify-center gap-4 min-h-[420px] cursor-pointer p-10 transition-all border-2 border-dashed ${dragOver ? "border-white bg-white/[0.04]" : "border-white/15 hover:border-white/40"}`}
            >
              <input
                ref={inputRef}
                id="ki-file"
                type="file"
                onChange={onFilePicked}
                accept={ALLOWED.join(",")}
                className="hidden"
                data-testid="ki-file-input"
              />
              <div className="w-16 h-16 border border-white/20 flex items-center justify-center">
                <Upload className="w-7 h-7 text-white" strokeWidth={1.4} />
              </div>
              <div className="text-center">
                <div className="font-display text-2xl md:text-3xl text-white tracking-tight mb-2">
                  {c.dropzone_title}
                </div>
                <div className="text-sm text-zinc-500">
                  {c.dropzone_sub}
                </div>
              </div>
              <span className="mt-2 inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 text-sm font-semibold shine">
                {c.choose_file} <ArrowRight className="w-4 h-4" />
              </span>
            </label>
          )}

          {/* --- STATE B: analyzing --- */}
          {busy && (
            <div data-testid="ki-analyzing" className="min-h-[420px] p-10 flex flex-col items-center justify-center gap-6">
              <div className="relative w-16 h-16 flex items-center justify-center">
                <Loader2 className="w-16 h-16 text-white/60 animate-spin" strokeWidth={1.2} />
                <Sparkles className="absolute w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <div className="text-center max-w-md">
                <div className="overline mb-2">{c.overline}</div>
                <div className="font-display text-xl md:text-2xl text-white tracking-tight mb-6">
                  {c.analyzing_steps[stepIdx] || c.fallback_analyzing}
                </div>
                <div className="flex items-center gap-1.5 justify-center">
                  {c.analyzing_steps.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1 transition-all ${i <= stepIdx ? "bg-white w-8" : "bg-white/15 w-4"}`}
                    />
                  ))}
                </div>
                {file && (
                  <div className="mt-8 text-[12px] text-zinc-500 inline-flex items-center gap-2">
                    <FileImage className="w-3.5 h-3.5" />
                    {file.name}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* --- STATE C: result --- */}
          {analysis && !busy && (
            <div data-testid="ki-result" className="grid grid-cols-1 lg:grid-cols-12">
              {/* LEFT · specs */}
              <div className="lg:col-span-7 p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-white/10">
                {/* file header */}
                <div className="flex items-center justify-between gap-4 mb-8 pb-6 border-b border-white/10">
                  <div className="flex items-center gap-3 min-w-0">
                    {file?.name?.toLowerCase().endsWith(".pdf") ? (
                      <FileText className="w-5 h-5 text-zinc-400" strokeWidth={1.4} />
                    ) : (
                      <FileImage className="w-5 h-5 text-zinc-400" strokeWidth={1.4} />
                    )}
                    <div className="truncate">
                      <div className="text-sm text-white truncate">{file?.name}</div>
                      <div className="text-[11px] text-zinc-500">
                        {file ? (file.size / 1024 / 1024).toFixed(2) + " MB" : ""}
                      </div>
                    </div>
                  </div>
                  <button type="button" onClick={reset} data-testid="ki-reset" className="text-zinc-400 hover:text-white p-1" aria-label="Reset">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="overline mb-6 flex items-center gap-2">
                  <Check className="w-3.5 h-3.5" />
                  {c.section_specs}
                </div>

                <dl className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
                  {[
                    [c.label_material, analysis.detected_material],
                    [c.label_geometry, analysis.detected_geometry],
                    [c.label_dimensions, analysis.detected_dimensions],
                    [c.label_threads, (analysis.detected_threads || []).join(", ") || "—"],
                    [c.label_tolerances, analysis.detected_tolerances],
                    [c.label_size, sizeDisplay(SIZE_CLASSES, analysis.estimated_size_class, lang)],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-[#121214] p-4">
                      <dt className="overline mb-1.5">{k}</dt>
                      <dd data-testid={`ki-spec-${k.toLowerCase()}`} className="text-sm text-white font-medium">
                        {v || "—"}
                      </dd>
                    </div>
                  ))}
                </dl>

                {/* Confidence + note */}
                <div className="mt-6 flex flex-wrap items-start gap-3">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-[11px] uppercase tracking-[0.14em] ${confColor}`}>
                    {analysis.confidence === "low" && <AlertTriangle className="w-3 h-3" />}
                    {c.label_confidence}: {confLabel}
                  </span>
                  {analysis.notes && (
                    <div className="flex-1 min-w-0 text-[12px] text-zinc-400 leading-relaxed">
                      <span className="overline mr-2">{c.label_notes}</span>
                      {analysis.notes}
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT · recommendation + price */}
              <div className="lg:col-span-5 p-8 md:p-12 bg-gradient-to-br from-[#0E0E10] to-[#141416] flex flex-col">
                <div className="overline mb-4">{c.section_recommendation}</div>
                <div className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight leading-[1] mb-3 result-rise">
                  {verfahrenDisplay(PRICING, analysis.recommended_verfahren, lang)}
                </div>
                <div className="h-px w-16 bg-white/40 mb-5 result-line" />
                <p className="text-zinc-300 text-sm md:text-base leading-relaxed mb-6">
                  {analysis.verfahren_reasoning || "—"}
                </p>

                {(analysis.alternate_verfahren || []).length > 0 && (
                  <div className="mb-6">
                    <div className="overline mb-2">{c.label_alternate}</div>
                    <div className="flex flex-wrap gap-2">
                      {(analysis.alternate_verfahren || []).map((k) => (
                        <span key={k} className="inline-flex items-center px-2.5 py-1 border border-white/15 text-[12px] text-zinc-200">
                          {verfahrenDisplay(PRICING, k, lang)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Price */}
                {pricing && (
                  <div className="border border-white/10 p-5 mb-6">
                    <div className="overline mb-3">{c.section_pricing}</div>
                    <div
                      data-testid="ki-price-range"
                      className="font-display text-2xl md:text-3xl text-white tracking-tight tabular-nums mb-1"
                    >
                      CHF {formatCHF(pricing.totalPriceMin)} – {formatCHF(pricing.totalPriceMax)}
                    </div>
                    <div className="text-[12px] text-zinc-400 mb-4">
                      {c.price_of}{" "}
                      <input
                        type="number"
                        min={1}
                        max={100000}
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                        data-testid="ki-qty"
                        className="w-20 bg-transparent border-b border-white/20 focus:border-white text-white tabular-nums outline-none text-center"
                      />{" "}
                      {c.price_of_pieces}
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      {c.qty_helper}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={requestOffer}
                  data-testid="ki-cta-offer"
                  className="group inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-4 text-sm font-semibold tracking-tight hover:bg-zinc-200 transition-colors duration-300 shine"
                >
                  {c.cta_offer}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>

                <button
                  type="button"
                  onClick={reset}
                  data-testid="ki-cta-reset"
                  className="mt-3 text-[12px] text-zinc-400 hover:text-white transition-colors"
                >
                  {c.cta_reset}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 text-[11px] text-zinc-500 leading-relaxed max-w-3xl">
          {c.disclaimer}
        </div>
      </div>
    </section>
  );
};

export default KiZeichnungsAnalyse;
